import { useId, useState, useCallback, useMemo, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import { Button } from '@/components/base/Button';
import { Input } from '@/components/base/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/base/Select';
import { SubcategoryPill } from '@/components/display/SubcategoryPill';
import XBoldIcon from '@/icons/XBold.svg?react';
import GoldenBuckeyeIcon from '@/icons/GoldenBuckeye.svg?react';
import CheckboxCheckedIcon from '@/icons/CheckboxChecked.svg?react';
import CheckboxUncheckedIcon from '@/icons/CheckboxUnchecked.svg?react';
import InfoIcon from '@/icons/Info.svg?react';
import type { FiltersModalProps } from './types';

/**
 * Collapsible section header component
 */
function CollapsibleHeader({
  title,
  subtitle,
  isExpanded,
  onToggle,
  id,
}: {
  title: string;
  subtitle?: string;
  isExpanded: boolean;
  onToggle: () => void;
  id: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      aria-controls={id}
      className={cn(
        'w-full flex items-start justify-between',
        'text-left cursor-pointer',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded'
      )}
    >
      <div className="flex flex-col">
        <span className={cn(typography.subtitle, 'text-foreground')}>
          {title}
        </span>
        {subtitle && (
          <span
            className={cn(
              'text-sm leading-5',
              "font-['Source_Sans_Pro']",
              'text-[var(--color-text-secondary)]'
            )}
          >
            {subtitle}
          </span>
        )}
      </div>
      {isExpanded ? (
        <ChevronUp className="h-5 w-5 text-foreground shrink-0" aria-hidden="true" />
      ) : (
        <ChevronDown className="h-5 w-5 text-foreground shrink-0" aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * FiltersModal - Modal version of the filters sidebar for tablet/mobile
 *
 * Contains the same filter controls as FiltersSidebar but displayed in a modal.
 * Does NOT include the feedback form section.
 * Uses a single scroll strategy: one outer scrollable area covers the location filter,
 * category filter, and subcategories list. The subcategories no longer have an independent
 * nested scroll — everything flows within the single scroll container.
 * Golden Buckeye checkbox and Apply button stay pinned at the bottom.
 *
 * @example
 * ```tsx
 * import { FiltersModal } from '@/components';
 *
 * <FiltersModal
 *   content={filtersContent}
 *   categories={categories}
 *   filterState={filterState}
 *   onFilterChange={handleFilterChange}
 *   onApplyFilters={handleApplyFilters}
 *   isOpen={showFiltersModal}
 *   onClose={() => setShowFiltersModal(false)}
 * />
 * ```
 */
export function FiltersModal({
  content,
  categories,
  filterState,
  disabledSubcategories = [],
  onFilterChange,
  onApplyFilters,
  isOpen,
  onClose,
  className,
}: FiltersModalProps) {
  const titleId = useId();

  // Collapsible section states
  const [locationExpanded, setLocationExpanded] = useState(true);
  const [categoryExpanded, setCategoryExpanded] = useState(true);

  // Detect mobile breakpoint (below 800px) - internal for portaled modals
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 799px)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };
    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Get current category's subcategories
  const currentCategory = useMemo(() => {
    return categories.find((c) => c.id === filterState.selectedCategory);
  }, [categories, filterState.selectedCategory]);

  const currentSubcategories = currentCategory?.subcategories || [];

  // Handlers
  const handleLocationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({ location: e.target.value });
    },
    [onFilterChange]
  );

  const handleLocationKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onApplyFilters();
        onClose();
      }
    },
    [onApplyFilters, onClose]
  );

  const handleDistanceChange = useCallback(
    (value: string) => {
      onFilterChange({ distance: value });
    },
    [onFilterChange]
  );

  const handleCategoryChange = useCallback(
    (value: string) => {
      const categoryId = value === 'all' ? null : value;
      // Auto-select all subcategories by default
      // When a specific category is selected, select all its subcategories
      // When "All categories" is selected, select all subcategories across all categories
      const selectedCategory = categories.find((c) => c.id === categoryId);
      const defaultSubcategories = selectedCategory
        ? selectedCategory.subcategories.map((sub) => sub.id)
        : categories.flatMap((cat) => cat.subcategories.map((sub) => sub.id));
      onFilterChange({
        selectedCategory: categoryId,
        selectedSubcategories: defaultSubcategories,
      });
    },
    [categories, onFilterChange]
  );

  const handleSubcategoryToggle = useCallback(
    (subcategoryId: string) => {
      const isSelected = filterState.selectedSubcategories.includes(subcategoryId);
      const newSubcategories = isSelected
        ? filterState.selectedSubcategories.filter((id) => id !== subcategoryId)
        : [...filterState.selectedSubcategories, subcategoryId];
      onFilterChange({ selectedSubcategories: newSubcategories });
    },
    [filterState.selectedSubcategories, onFilterChange]
  );

  const handleSelectAll = useCallback(() => {
    const subcategoriesToSelect =
      filterState.selectedCategory === null
        ? categories.flatMap((cat) => cat.subcategories.map((sub) => sub.id))
        : currentSubcategories.map((sub) => sub.id);
    onFilterChange({
      selectedSubcategories: subcategoriesToSelect,
    });
  }, [categories, currentSubcategories, filterState.selectedCategory, onFilterChange]);

  const handleDeselectAll = useCallback(() => {
    onFilterChange({ selectedSubcategories: [] });
  }, [onFilterChange]);

  const handleGoldenBuckeyeToggle = useCallback(() => {
    onFilterChange({ includeGoldenBuckeye: !filterState.includeGoldenBuckeye });
  }, [filterState.includeGoldenBuckeye, onFilterChange]);

  const handleApplyAndClose = useCallback(() => {
    onApplyFilters();
    onClose();
  }, [onApplyFilters, onClose]);

  if (!content) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/50 z-[130]"
          data-testid="filters-modal-overlay"
          onClick={onClose}
        />
        <Dialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'bg-white',
            'border border-[#E9E9E9]',
            'pt-6 pr-4 pb-4 pl-4',
            'flex flex-col',
            'z-[131]',
            'focus:outline-none',
            'overflow-hidden',
            isMobile
              ? 'w-[calc(100vw-32px)] max-w-[400px] h-auto max-h-[calc(100vh-64px)]'
              : 'w-[640px] h-[852px] max-h-[calc(100vh-64px)]',
            className
          )}
          style={{
            boxShadow: '0px 4px 4px 0px rgba(160, 160, 160, 0.25)',
          }}
          aria-labelledby={titleId}
          data-testid="filters-modal-content"
        >
          {/* Close Button */}
          <Dialog.Close
            className="absolute top-[22px] right-[22px] w-5 h-5 flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer"
            aria-label="Close filters"
            data-testid="filters-modal-close"
          >
            <XBoldIcon className="w-full h-full text-black" aria-hidden="true" />
          </Dialog.Close>

          {/* Filters title */}
          <Dialog.Title
            id={titleId}
            className={cn(typography.headingSmall, 'mb-4 text-foreground shrink-0')}
          >
            {content.title}
          </Dialog.Title>

          {/* Single scroll area — covers location filter, category filter, and subcategories */}
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
            {/* Location and distance filter */}
            <div className="shrink-0 border-b border-[var(--color-border-default)] pb-6 mb-6">
              <CollapsibleHeader
                title={content.locationFilter.title}
                subtitle={content.locationFilter.subtitle}
                isExpanded={locationExpanded}
                onToggle={() => setLocationExpanded(!locationExpanded)}
                id="modal-location-filter"
              />
              {locationExpanded && (
                <div id="modal-location-filter" className="mt-3 flex flex-col gap-3">
                  <Input
                    type="text"
                    placeholder={content.locationFilter.addressPlaceholder}
                    value={filterState.location}
                    onChange={handleLocationChange}
                    onKeyDown={handleLocationKeyDown}
                    aria-label="Location address or zip code"
                    className="w-full input-service-directory"
                  />
                  <Select value={filterState.distance} onValueChange={handleDistanceChange}>
                    <SelectTrigger
                      className="w-full input-service-directory"
                      aria-label="Select distance"
                    >
                      <SelectValue placeholder={content.locationFilter.defaultDistance} />
                    </SelectTrigger>
                    <SelectContent>
                      {content.locationFilter.distanceOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Category filter */}
            <div className="border-b border-[var(--color-border-default)] mb-4 flex flex-col">
              <CollapsibleHeader
                title={content.categoryFilter.title}
                isExpanded={categoryExpanded}
                onToggle={() => setCategoryExpanded(!categoryExpanded)}
                id="modal-category-filter"
              />
              {categoryExpanded && (
                <div id="modal-category-filter" className="mt-3 flex flex-col gap-3">
                  {/* Category dropdown */}
                  <Select
                    value={filterState.selectedCategory || 'all'}
                    onValueChange={handleCategoryChange}
                  >
                    <SelectTrigger
                      className="w-full input-service-directory"
                      aria-label="Select category"
                    >
                      <SelectValue placeholder="All categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All categories</SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Subcategories section */}
                  {(filterState.selectedCategory === null ||
                    currentSubcategories.length > 0) && (
                    <div className="mt-2 flex flex-col">
                      {/* Subcategories header */}
                      <div className="flex items-center justify-between pb-3 border-b border-[var(--color-border-default)]">
                        <span className={cn(typography.buttonDefault, 'text-foreground')}>
                          {content.categoryFilter.subcategoriesTitle}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleSelectAll}
                            className={cn(
                              typography.buttonDefault,
                              'text-[var(--color-primary-brand)] hover:text-[var(--color-primary-brand-hover)]',
                              'hover:underline cursor-pointer',
                              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded'
                            )}
                          >
                            {content.categoryFilter.selectAllText}
                          </button>
                          <span
                            className="w-px h-[30px] bg-[#EBEBEB]"
                            aria-hidden="true"
                          ></span>
                          <button
                            type="button"
                            onClick={handleDeselectAll}
                            className={cn(
                              typography.buttonDefault,
                              'text-[var(--color-primary-brand)] hover:text-[var(--color-primary-brand-hover)]',
                              'hover:underline cursor-pointer',
                              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded'
                            )}
                          >
                            {content.categoryFilter.deselectAllText}
                          </button>
                        </div>
                      </div>

                      {/* Subcategories list — flows within the single outer scroll area */}
                      <div className="pt-3 pb-4 pr-2">
                        {/* Warning banner when subcategories are disabled */}
                        {disabledSubcategories.length > 0 && (
                          <div
                            className="mb-4 rounded-[4px] p-4 flex items-start gap-3"
                            style={{
                              background: '#F3DF89',
                              border: '1px solid #F3DF89',
                            }}
                            role="alert"
                          >
                            <InfoIcon
                              className="w-8 h-8 shrink-0"
                              aria-hidden="true"
                            />
                            <p
                              className="m-0"
                              style={{
                                fontFamily: "'Source Sans Pro', sans-serif",
                                fontWeight: 600,
                                fontSize: '20px',
                                lineHeight: '28px',
                                color: '#000000',
                              }}
                            >
                              For select subcategories below, no relevant services were found in your area. Try adjusting filters, expanding your search range, or contacting your local Area Agency on Aging (AAA) for more options
                            </p>
                          </div>
                        )}
                        {filterState.selectedCategory === null ? (
                          <div className="space-y-4">
                            {categories.map((category) => (
                              <div key={category.id}>
                                <span
                                  className={cn(
                                    typography.buttonDefault,
                                    'text-black block mb-2'
                                  )}
                                >
                                  {category.name}
                                </span>
                                <div className="flex flex-wrap gap-2">
                                  {category.subcategories.map((subcategory) => (
                                    <SubcategoryPill
                                      key={subcategory.id}
                                      subcategory={subcategory}
                                      isSelected={filterState.selectedSubcategories.includes(
                                        subcategory.id
                                      )}
                                      isDisabled={disabledSubcategories.includes(subcategory.id)}
                                      onClick={() =>
                                        handleSubcategoryToggle(subcategory.id)
                                      }
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <>
                            {currentCategory && (
                              <span
                                className={cn(
                                  typography.buttonDefault,
                                  'text-black block mb-2'
                                )}
                              >
                                {currentCategory.name}
                              </span>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {currentSubcategories.map((subcategory) => (
                                <SubcategoryPill
                                  key={subcategory.id}
                                  subcategory={subcategory}
                                  isSelected={filterState.selectedSubcategories.includes(
                                    subcategory.id
                                  )}
                                  isDisabled={disabledSubcategories.includes(subcategory.id)}
                                  onClick={() => handleSubcategoryToggle(subcategory.id)}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Golden Buckeye filter - fixed above button */}
          <div className="mt-2 mb-4 shrink-0 pt-1">
            <div
              role="checkbox"
              aria-checked={filterState.includeGoldenBuckeye}
              tabIndex={0}
              onClick={handleGoldenBuckeyeToggle}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  handleGoldenBuckeyeToggle();
                }
              }}
              className={cn(
                'flex items-start gap-3 cursor-pointer',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded p-1 -m-1'
              )}
            >
              <div className="mt-0.5 shrink-0">
                {filterState.includeGoldenBuckeye ? (
                  <CheckboxCheckedIcon className="w-5 h-5" aria-hidden="true" />
                ) : (
                  <CheckboxUncheckedIcon className="w-5 h-5" aria-hidden="true" />
                )}
              </div>
              <div className="flex items-start gap-2">
                <GoldenBuckeyeIcon
                  className="h-6 w-6 text-[var(--color-golden-buckeye)] shrink-0"
                  aria-hidden="true"
                />
                <span className={cn(typography.buttonDefault, 'text-black select-none')}>
                  {content.goldenBuckeyeFilter.text}
                </span>
              </div>
            </div>
          </div>

          {/* Apply filters button - fixed at bottom */}
          <div className="shrink-0">
            <Button
              variant="primary"
              size="large"
              content={{ text: content.applyButton.text }}
              onClick={handleApplyAndClose}
              disabled={!filterState.location.trim()}
              className="w-full"
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

FiltersModal.displayName = 'FiltersModal';
